<template>
  <modal :show.sync="show">
    <h6 slot="header" class="modal-title" id="modal-title-default">
      <i :class="['mr-2', iconClass]"></i>{{ title }}
    </h6>
    
    <template>
      <form @submit.prevent> 
        <div class="hours-container">
          <div class="hours-header">
            <div class="hours-day">Day</div>
            <div class="hours-time">{{ fromLabel }}</div>
            <div class="hours-time">{{ toLabel }}</div>
          </div>
          
          <div 
            class="hours-item" 
            v-for="(hours, index) in hoursData" 
            :key="index"
          >
            <div class="hours-day">
              <span class="day-badge" :class="getDayClass(hours.day)">
                {{ hours.day }}
              </span>
            </div>
            <div class="hours-time">{{ formatTimeTo12Hour(hours.from) }}</div>
            <div class="hours-time">{{ formatTimeTo12Hour(hours.to) }}</div>
          </div>
          
          <div v-if="!hoursData || hoursData.length === 0" class="no-hours">
            <i :class="iconClass + ' text-muted'"></i>
            <p>No {{ type === 'business' ? 'business' : 'break' }} hours defined</p>
          </div>
        </div>
      </form>
    </template>

    <template slot="footer">
      <base-button type="default" @click="closeModal">
        Close
      </base-button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import BaseButton from "@/components/BaseButton";

export default {
  name: 'HoursModal',
  components: {
    Modal,
    BaseButton
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'business',
      validator: value => ['business', 'leisure'].includes(value)
    },
    hoursData: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    title() {
      return this.type === 'business' ? 'Business Hours' : 'Break Hours';
    },
    iconClass() {
      return this.type === 'business' 
        ? 'ni ni-time-alarm text-primary' 
        : 'ni ni-coffee-grain text-warning';
    },
    fromLabel() {
      return this.type === 'business' ? 'Opening Time' : 'From';
    },
    toLabel() {
      return this.type === 'business' ? 'Closing Time' : 'To';
    }
  },
  methods: {
    closeModal() {
      this.$emit('update:show', false);
    },
    getDayClass(day) {
      const dayMap = {
        'Monday': 'badge-primary',
        'Tuesday': 'badge-info',
        'Wednesday': 'badge-success',
        'Thursday': 'badge-warning',
        'Friday': 'badge-danger',
        'Saturday': 'badge-default',
        'Sunday': 'badge-secondary'
      };
      
      return dayMap[day] || 'badge-light';
    },
    formatTimeTo12Hour(time24) {
      if (!time24) return '';
      
      // Parse the time in 24-hour format (HH:MM)
      const [hours24, minutes] = time24.split(':').map(Number);
      
      // Convert to 12-hour format
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const hours12 = hours24 % 12 || 12; // Convert 0 to 12 for 12 AM
      
      // Format the result as "hh:mm AM/PM"
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  }
}
</script>

<style scoped>
.hours-container {
  margin: 1rem 0;
}

.hours-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 0.5rem;
  background-color: #f8f9fe;
  border-radius: 0.375rem 0.375rem 0 0;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #8898aa;
}

.hours-item {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
}

.hours-item:last-child {
  border-bottom: none;
}

.hours-day {
  display: flex;
  align-items: center;
}

.day-badge {
  padding: 0.35rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
}

.hours-time {
  font-size: 0.875rem;
  color: #525f7f;
}

.no-hours {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  color: #8898aa;
}

.no-hours i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.no-hours p {
  margin: 0;
  font-size: 0.875rem;
}
</style>
